import {
  AccountData,
  ConversationPreset,
  FollowerStatus,
  IMessage,
  PlanTypeV4,
} from "@markit/common.types";
import { addAlpha, Colors } from "../../../utils/colors";
import ConversationAttachmentButton from "./ConversationAttachmentButton";
import { CircularProgress, TextareaAutosize } from "@mui/material";
import AlertButtonWrapper from "../../Containers/AlertButtonWrapper";
import { unsubscribedUserAlertText } from "../../../utils/alertUtils";
import { Icon } from "@iconify/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  censoredWordsDetected,
  detectedCensored,
  hasSubscriptionUnlockedAdvancedData,
} from "@markit/common.utils";
import { API } from "../../../API";
import { useDispatch, useSelector } from "react-redux";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import ConfirmActionModal from "../../Containers/ConfirmPopups/ConfirmActionModal";
import { MixpanelContext } from "../../../context/AnalyticsService";
import { saveMediaToFirestore } from "../../../utils/photoUtils";
import { generate } from "shortid";
import ExpandableBox from "../../Containers/ExpandableBox";
import TextHoverButton from "../../Buttons/TextHoverButton";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { getConversationState } from "../../../redux/slices/conversationSlice";

const AI_GENERATED = "ai-generated";

type ConversationMessageInputProps = {
  conversationsMainUser: AccountData | undefined;
  scrollToBottom: () => void;
  messages: IMessage[];
  loadingConversation: boolean;
  addTextPreset?: () => void;
};

const ConversationMessageInput = (props: ConversationMessageInputProps) => {
  const {
    conversationsMainUser,
    scrollToBottom,
    messages,
    loadingConversation,
    addTextPreset,
  } = props;
  const { accountData, followersData } = useSelector(getAccountSlice);
  const { conversationPresets } = useSelector(getConversationState);
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const [alertText, setAlertText] = useState({ header: "", subHeader: "" });
  const [confirmSendImage, setConfirmSendImage] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [image, setImage] = useState<string>("");
  const [showPresetsToolbar, setShowPresetsToolbar] = useState(true);
  const [aiGeneratedPreset, setAiGeneratedPreset] =
    useState<ConversationPreset>({
      id: AI_GENERATED,
      preset: "",
      createdAt: new Date().toISOString(),
    });
  const [isGeneratingAIResponse, setIsGeneratingAIResponse] = useState(false);
  const hasInitializedPresets = useRef(false);

  const styles = {
    presetContainer: {
      padding: "10px 14px",
      cursor: "pointer",
      height: 62,
      width: 200,
      borderRadius: 14,
      flexShrink: 0,
      overflow: "hidden",
    },
  };

  useEffect(() => {
    if (image) {
      setConfirmSendImage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  const followerData = useMemo(
    () =>
      followersData.find(
        (follower) =>
          conversationsMainUser && follower.uid === conversationsMainUser.uid
      ),
    [conversationsMainUser, followersData]
  );

  const confirmSendMessage = useCallback(() => {
    if (detectedCensored(message)) {
      setAlertText({
        header: "Content Violation!",
        subHeader: `You used the following banned terms in your message: '${censoredWordsDetected(
          message
        )}'. Remove these terms and resend.`,
      });
      return false;
    }
    return true;
  }, [message]);

  const sendTextMessage = useCallback(async () => {
    if (!confirmSendMessage()) {
      return;
    }
    setIsSendingMessage(true);
    setMessage("");
    await API.creatorText
      .sendMessageToUser({
        twilioUser: accountData,
        attendeeUser: conversationsMainUser,
        message: message,
        mediaUrl: "",
      })
      .then(async () => {
        if (mixpanel) {
          mixpanel.track("Conversation: Creator Sent Message To Participant", {
            distinct_id: accountData.uid,
            recipient_id: conversationsMainUser?.uid,
            message: message,
            from: "webapp",
          });
        }
        setIsSendingMessage(false);
        scrollToBottom();
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, [
    accountData,
    confirmSendMessage,
    conversationsMainUser,
    message,
    mixpanel,
    scrollToBottom,
  ]);

  const sendImage = useCallback(async () => {
    if (!confirmSendMessage()) {
      setImage("");
      return;
    }
    let mediaUrl = "";
    if (image !== "") {
      setIsSendingMessage(true);
      const url = await saveMediaToFirestore(
        image,
        accountData.uid + "/textMedia/chats/" + generate(),
        accountData.customer.contactCardUrl
      );
      mediaUrl = url;

      await API.creatorText
        .sendMessageToUser({
          twilioUser: accountData,
          attendeeUser: conversationsMainUser,
          message: "\u00A0", // Can't send an empty string as message so send blank character
          mediaUrl: mediaUrl,
        })
        .then(async () => {
          if (mixpanel) {
            mixpanel.track(
              "Conversation: Creator Sent Message To Participant",
              {
                recipient_id: conversationsMainUser?.uid ?? "",
                from: "webapp",
                message: "Attachment: 1 Image",
                mediaUrl: mediaUrl,
              }
            );
          }
          setIsSendingMessage(false);
          setImage("");
          scrollToBottom();
        })
        .catch((err) => {
          console.log(err.message);
          setIsSendingMessage(false);
          setImage("");
        });
    }
  }, [
    confirmSendMessage,
    image,
    accountData,
    conversationsMainUser,
    mixpanel,
    scrollToBottom,
  ]);

  const conversationPresetOnPress = useCallback(
    (preset: ConversationPreset) => {
      if (
        preset.id === AI_GENERATED &&
        !hasSubscriptionUnlockedAdvancedData(accountData)
      ) {
        dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name));
        return;
      }
      if (mixpanel && preset.id === AI_GENERATED) {
        mixpanel.track("Selected Conversation AI Response", {
          distinct_id: accountData.uid,
          ai_preset: preset.preset,
          conversation_user_id: conversationsMainUser?.uid,
        });
      }
      setMessage((message ? message + " " : "") + preset.preset);
      setShowPresetsToolbar(false);
    },
    [accountData, conversationsMainUser?.uid, dispatch, message, mixpanel]
  );

  // Generate AI response for the conversation chat
  const generateAIResponse = useCallback(async () => {
    if (!conversationsMainUser) {
      return;
    }

    setIsGeneratingAIResponse(true);
    try {
      const { generatedResponse } =
        await API.conversation.generateConversationAIResponse({
          messages,
          creatorFullName: accountData.fullName,
          followerFullName: conversationsMainUser.fullName,
        });

      if (generatedResponse && generatedResponse.trim() !== "") {
        const aiPreset: ConversationPreset = {
          id: AI_GENERATED,
          preset: generatedResponse,
          createdAt: new Date().toISOString(),
        };
        setAiGeneratedPreset(aiPreset);
      }
    } catch (error: any) {
      console.error("Error generating AI response:", error);
    } finally {
      setIsGeneratingAIResponse(false);
    }
  }, [conversationsMainUser, messages, accountData]);

  const allPresets = useMemo(() => {
    if (aiGeneratedPreset) {
      return [aiGeneratedPreset, ...conversationPresets];
    }
    return conversationPresets;
  }, [aiGeneratedPreset, conversationPresets]);

  const presetToolbarOnPress = useCallback(async () => {
    scrollToBottom();
    setShowPresetsToolbar(!showPresetsToolbar);
  }, [scrollToBottom, showPresetsToolbar]);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (_, isLatest) => {
      // Show the presets toolbar by default, unless it's a brand new conversation, or already mounted
      if (
        !conversationsMainUser ||
        hasInitializedPresets.current ||
        loadingConversation
      ) {
        return;
      }

      hasInitializedPresets.current = true;
      setShowPresetsToolbar(true);

      if (isLatest()) {
        // Keep the newest messages visible as the toolbar opens and AI starts loading.
        scrollToBottom();
        requestAnimationFrame(() => {
          if (isLatest()) {
            scrollToBottom();
          }
        });

        // Generate the AI response when you first open a conversation chat
        if (hasSubscriptionUnlockedAdvancedData(accountData)) {
          await generateAIResponse();
        }
      }
    },
    [
      accountData,
      conversationsMainUser,
      generateAIResponse,
      loadingConversation,
      scrollToBottom,
    ]
  );

  const renderPresetContainer = useCallback(
    (preset: ConversationPreset) => {
      const isGated =
        preset.id === AI_GENERATED &&
        !hasSubscriptionUnlockedAdvancedData(accountData);

      return (
        <div
          key={preset.id}
          className="ColumnNormal"
          style={{
            ...styles.presetContainer,
            border: `0.5px solid ${
              preset.id === AI_GENERATED ? Colors.PINK2 : Colors.GRAY2
            }`,
            color: preset.id === AI_GENERATED ? Colors.BLACK : Colors.BLUE5,
            backgroundColor:
              preset.id === AI_GENERATED
                ? addAlpha(Colors.PINK2, 0.1)
                : Colors.WHITE,
            gap: 4,
          }}
          onClick={() => conversationPresetOnPress(preset)}
          onMouseEnter={
            !isGated
              ? (e) => {
                  if (preset.id === AI_GENERATED) {
                    e.currentTarget.style.opacity = "0.7";
                  } else {
                    e.currentTarget.style.backgroundColor = Colors.BLUE5;
                    e.currentTarget.style.color = Colors.WHITE;
                  }
                }
              : undefined
          }
          onMouseLeave={
            !isGated
              ? (e) => {
                  if (preset.id === AI_GENERATED) {
                    e.currentTarget.style.opacity = "1";
                  } else {
                    e.currentTarget.style.backgroundColor = Colors.WHITE;
                    e.currentTarget.style.color = Colors.BLUE5;
                  }
                }
              : undefined
          }
        >
          <div
            className="ColumnCentering"
            style={{
              flex: 1,
              minHeight: preset.id === AI_GENERATED ? 46 : undefined,
            }}
          >
            {isGated ? (
              <span
                style={{
                  fontSize: 13,
                  color: isGated ? Colors.GRAY1 : Colors.BLACK,
                }}
              >
                AI-assistive messaging requires a Markit+ Pro Plan
              </span>
            ) : preset.id === AI_GENERATED && isGeneratingAIResponse ? (
              <CircularProgress size={14} style={{ color: Colors.PINK2 }} />
            ) : (
              <span
                style={{
                  fontSize: 13,
                  display: "-webkit-box",
                  WebkitLineClamp: preset.id === AI_GENERATED ? 3 : 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  whiteSpace: "pre-line",
                }}
              >
                {preset.preset}
              </span>
            )}
          </div>
          {preset.id === AI_GENERATED ? (
            <div className="AlignedRowSpaced">
              <span style={{ fontSize: 11, color: Colors.GRAY1 }}>
                {isGated ? "Get Markit+ Pro" : "AI Generated"}
              </span>
              {isGated ? (
                <div className="AlignedRow" style={{ gap: 2 }}>
                  <span style={{ fontSize: 11, color: Colors.PINK2 }}>
                    Upgrade
                  </span>
                  <Icon
                    icon="ion:chevron-forward"
                    height={11}
                    style={{ marginTop: 1, color: Colors.PINK2 }}
                  />
                </div>
              ) : !isGeneratingAIResponse ? (
                <TextHoverButton
                  text="Regen"
                  icon={
                    <Icon
                      icon="ion:sync-sharp"
                      height={13}
                      color={Colors.PINK2}
                    />
                  }
                  onPress={(e: any) => {
                    e.stopPropagation();
                    generateAIResponse();
                  }}
                  textStyles={{ fontSize: 11, color: Colors.PINK2 }}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      );
    },
    [
      accountData,
      conversationPresetOnPress,
      generateAIResponse,
      isGeneratingAIResponse,
      styles.presetContainer,
    ]
  );

  return (
    <div
      className="ColumnNormal"
      style={{ flex: 1, paddingInline: 14, overflow: "hidden" }}
    >
      <ExpandableBox>
        {showPresetsToolbar && allPresets.length > 0 ? (
          <div
            className="AlignedRow"
            style={{
              gap: 10,
              overflowX: "auto",
              flexShrink: 0,
              width: "100%",
              paddingBottom: 14,
            }}
          >
            {allPresets.map((preset) => renderPresetContainer(preset))}
            {addTextPreset ? (
              <div
                className="Centering"
                style={{
                  ...styles.presetContainer,
                  border: `0.5px solid ${Colors.GRAY2}`,
                  backgroundColor: Colors.WHITE,
                  color: Colors.BLUE5,
                }}
                onClick={addTextPreset}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = Colors.BLUE5;
                  e.currentTarget.style.color = Colors.WHITE;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = Colors.WHITE;
                  e.currentTarget.style.color = Colors.BLUE5;
                }}
              >
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <Icon icon="ion:add-circle" height={16} />
                  <span className="bodyRegular" style={{ fontSize: 13 }}>
                    Add Text Preset
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </ExpandableBox>
      <div className="RowNormal" style={{ gap: 7, alignItems: "flex-end" }}>
        <ConversationAttachmentButton
          message={message}
          setMessage={setMessage}
          image={image}
          setImage={setImage}
          conversationMainUserId={conversationsMainUser?.uid ?? ""}
        />
        {conversationsMainUser ? (
          <div
            className="Centering CreatorChatAttachmentButton"
            style={{
              border: `0.5px solid ${
                showPresetsToolbar ? Colors.BLUE5 : Colors.GRAY1
              }`,
              backgroundColor: showPresetsToolbar ? Colors.BLUE5 : Colors.WHITE,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            onClick={presetToolbarOnPress}
          >
            <Icon
              icon="ion:sparkles"
              height={18}
              style={{
                color: showPresetsToolbar ? Colors.WHITE : Colors.BLUE5,
              }}
            />
          </div>
        ) : null}
        <div className="ConversationsInputContainer">
          <TextareaAutosize
            id="conversationmainmessage"
            value={message}
            placeholder={"Send a message..."}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            className="ConversationsInput HideScrollbar"
            maxRows={15}
          />
          <AlertButtonWrapper
            buttonComp={
              <div
                onClick={() => {
                  followerData &&
                  followerData.status !== FollowerStatus.SUBSCRIBED
                    ? setAlertText({
                        header: unsubscribedUserAlertText(followerData),
                        subHeader: "",
                      })
                    : sendTextMessage();
                }}
                style={{
                  pointerEvents:
                    isSendingMessage || message === "" ? "none" : "all",
                  cursor: "pointer",
                  position: "absolute",
                  bottom: 5,
                  right: 5,
                  display: "flex",
                  alignItems: isSendingMessage ? "center" : "flex-end",
                }}
              >
                {isSendingMessage ? (
                  <div style={{ paddingInline: 8, paddingBlock: 6 }}>
                    <span className="bodySubtext">Sending...</span>
                  </div>
                ) : (
                  <div
                    className="CreatorChatSendButton"
                    style={{
                      backgroundColor:
                        message === "" ? Colors.GRAY1 : Colors.BLACK,
                      cursor: message === "" ? "not-allowed" : "pointer",
                    }}
                  >
                    <Icon
                      icon="ion:paper-plane"
                      height={18}
                      style={{
                        paddingRight: 2,
                        paddingTop: 2,
                        color: Colors.WHITE,
                      }}
                    />
                  </div>
                )}
              </div>
            }
            alertTextHeader={alertText.header}
            alertTextSubHeader={alertText.subHeader}
            clearAlert={() => setAlertText({ header: "", subHeader: "" })}
          />
        </div>
        <ConfirmActionModal
          heading="Ready to Send?"
          confirmButtonText="Send"
          icon={<Icon icon="ion:paper-plane" height={40} />}
          hideModal={!confirmSendImage}
          setIsVisible={setConfirmSendImage}
          confirmOnPress={sendImage}
          cancelOnPress={() => setImage("")}
        />
      </div>
    </div>
  );
};

export default ConversationMessageInput;
